import { useFormik } from "formik";
import { Button } from "primereact/button";

import * as Yup from "yup";
import { useTranslation } from "@i18n/hooks";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { ValidatedField } from "../../../../components/ValidatedField";
import { WarningUnsavedChangesDialog } from "../../../../components/WarningUnsavedChangesDialog";
import {
	OfferFileSelectionField,
	type FileV2,
} from "../../../../features/offer-files/offer-file-selection-field";
import { useHasPermissions } from "../../../../hooks/use-has-permissions";
import { useCallbackPrompt } from "../../../../hooks/UseCallBackPrompt";
import {
	addEditedFormToEditedFormsArray,
	resetArrayOfEditedForms,
	selectArrayOfEditedForms,
} from "../../../../store/slices/CoreSlice";
import { useTypedDispatch, useTypedSelector } from "../../../../store/store.redux";

export type FormState = {
	name: string;
	position?: string;
	shortDescription?: string;
	description?: string;
	image?: FileV2;
};

type OwnProps = {
	loading?: boolean;
	initialValues?: FormState;
	onSubmit: (values: FormState) => void;
};

export const EditInstructorForm = ({ loading, initialValues, onSubmit }: OwnProps) => {
	const { t } = useTranslation(["common", "validation"]);
	const dispatch = useTypedDispatch();
	const arrayOfEditedForms = useTypedSelector(selectArrayOfEditedForms);
	const [showPrompt, confirmNavigation, cancelNavigation, setShowPrompt] = useCallbackPrompt(
		arrayOfEditedForms.length > 0,
	);

	const formId = "EditInstructorForm";
	const formik = useFormik<FormState>({
		initialValues: {
			name: initialValues?.name ?? t("common:instructor.newTrainer"),
			position: initialValues?.position,
			shortDescription: initialValues?.shortDescription,
			description: initialValues?.description,
			image: initialValues?.image as FileV2,
		},
		validationSchema: Yup.object().shape({
			name: Yup.string().required(t("validation:instructorNameRequired")),
		}),
		onSubmit: (values, { setSubmitting }) => {
			onSubmit({
				...values,
			});
			setSubmitting(false);
			dispatch(resetArrayOfEditedForms());
		},
	});

	const canUpload = useHasPermissions(["UserInAccountPermission_Instructors_UpdateInstructors"]);

	const canDelete = useHasPermissions("onlyOwnerOfRoot");

	return (
		<>
			{showPrompt && (
				<WarningUnsavedChangesDialog
					confirmNavigation={confirmNavigation}
					setShowDialog={setShowPrompt}
					cancelNavigation={cancelNavigation}
				/>
			)}
			<form onSubmit={formik.handleSubmit} className="p-fluid">
				<ValidatedField<FormState, string>
					name={"name"}
					label={t("common:table.name")}
					component={DefaultTextFieldComponent}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					formikConfig={formik}
				/>
				<ValidatedField<FormState, string>
					name={"position"}
					label={t("common:instructor.position")}
					component={DefaultTextFieldComponent}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					formikConfig={formik}
				/>
				<ValidatedField<FormState, string>
					name={"shortDescription"}
					label={t("common:instructor.shortDescription")}
					component={DefaultTextFieldComponent}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					formikConfig={formik}
				/>
				<ValidatedField<FormState, string>
					name={"description"}
					label={t("common:forms.description")}
					component={DefaultTextFieldComponent}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					formikConfig={formik}
				/>
				<ValidatedField<FormState, FileV2>
					name={"image"}
					label={t("common:instructor.profileImage")}
					helpText={t("common:instructor.profileImage")}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					component={({ fieldName, fieldValue, updateField, onChange }) => (
						<OfferFileSelectionField
							name={fieldName}
							selectedFile={fieldValue}
							setSelectedFile={updateField}
							filterByFileTypes={["image/png", "image/jpg", "image/jpeg"]}
							onChange={onChange}
							canUploadFiles={canUpload}
							canDeleteFiles={canDelete}
						/>
					)}
					formikConfig={formik}
				/>
				<Button
					disabled={loading}
					type="submit"
					label={t("common:actions.save")}
					className="p-mt-2"
				/>
			</form>
		</>
	);
};
